/**
 * CredentialActionsSection — the "Access" section of a service account record:
 * the credential's own actions, so the slideout is where a credential is
 * managed and not only viewed.
 *
 *  - New token: mints a further bearer token (one-time reveal modal). The ONLY
 *    UI path that can, because `ahoy_principal_to_secret` is internal-read and
 *    the generic record editor never sees a Secrets row.
 *  - Delete: sweeps the credential's secrets, then deletes it, then closes the
 *    slideout. The header's generic delete is switched off for these types
 *    (`showRecordActionMenu: false`) because it would orphan the secrets.
 *
 * Registered as a `custom` type-specific section on the service-account
 * credential entry.
 */

import { useQueryClient } from '@tanstack/react-query';
import { Key01, Trash01 } from '@untitledui/icons';
import { useCallback, useState } from 'react';

import { usePermissions } from '@/hooks/usePermissions';
import {
  credentialReachOf,
  getCredentialName,
} from '@/lib/api-access/api-access-service';
import {
  CREDENTIAL_NOUN,
  useDeleteCredential,
} from '@/lib/api-access/use-delete-credential';
import { useMintToken } from '@/lib/api-access/use-mint-token';
import { Button } from '@/lib/base/buttons/button';
import { ConfirmationDialog } from '@/lib/base/overlays';
import { WebhookSecretCreatedModal } from '@/lib/crm/components/webhooks/WebhookSecretCreatedModal';
import { useSlideoutStack } from '@/lib/crm/hooks/useSlideoutStack';
import { crmQueryKeys } from '@/lib/crm/query/queries';
import type { CrmObject } from '@/types/crm';

import { CollapsibleSection } from '../sections/collapsible-section';

const CREDENTIAL_TYPES = ['ahoy_service_account'] as const;

export function CredentialActionsSection({ object }: { object: CrmObject }) {
  const orgId = object.org_id;
  const queryClient = useQueryClient();
  const { popSlideout } = useSlideoutStack();
  const { hasPermission } = usePermissions();
  const reach = credentialReachOf(object);
  const name = getCredentialName(object);
  const noun = CREDENTIAL_NOUN;
  // Delete is unrestricted for ahoy_schema_admin (permission_groups.yaml's
  // ahoy_service_account block) — deleting destroys access rather than
  // granting it, so it stays admin-wide.
  const canDelete = hasPermission(
    'ahoy_service_account',
    'ahoy_object_deleted'
  );
  // Minting attaches a new secret via an `ahoy_principal_to_secret`
  // association FROM the service account (see useMintToken's attachSecret
  // call), which permission_groups.yaml scopes to the credential's creator
  // via `ahoy_service_account_to_creator` (ahoy_association_added). There's
  // no per-entity creator signal cheaply available on the record payload, so
  // this gates on the coarser unscoped grant — same shape as
  // useOutboundChannelState's canToggle — and a genuine creator-scoping
  // rejection for a non-creator surfaces through the mint's existing toast
  // error path.
  const canMint = hasPermission(
    'ahoy_service_account',
    'ahoy_association_added'
  );

  // The settings list is a different query from this record; refresh both
  // credential lists so a mint/edit/delete here is reflected there.
  const invalidateLists = useCallback(() => {
    for (const type of CREDENTIAL_TYPES) {
      queryClient.invalidateQueries({
        queryKey: crmQueryKeys.objects(orgId, type),
      });
    }
  }, [queryClient, orgId]);

  const { mintToken, mintingCredentialId, mintedToken, dismissMintedToken } =
    useMintToken(orgId, invalidateLists);
  const isMinting = mintingCredentialId === object.entity_id;

  const [isMintConfirmOpen, setIsMintConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const openMintConfirm = useCallback(() => setIsMintConfirmOpen(true), []);
  const closeMintConfirm = useCallback(() => setIsMintConfirmOpen(false), []);
  const handleConfirmMint = useCallback(() => {
    setIsMintConfirmOpen(false);
    mintToken(object);
  }, [mintToken, object]);

  const openDeleteConfirm = useCallback(() => setIsDeleteConfirmOpen(true), []);
  const closeDeleteConfirm = useCallback(
    () => setIsDeleteConfirmOpen(false),
    []
  );
  const handleDeleted = useCallback(() => {
    invalidateLists();
    popSlideout();
  }, [invalidateLists, popSlideout]);
  const { isDeleting, deleteWithSecrets } = useDeleteCredential({
    orgId,
    credentialEntityId: object.entity_id,
    entityTypeName: object.entity_type_name,
    lastEventSeq: object.last_event_seq,
    onDeleted: handleDeleted,
  });
  const handleConfirmDelete = useCallback(async () => {
    await deleteWithSecrets();
  }, [deleteWithSecrets]);

  return (
    <>
      <CollapsibleSection title='Access'>
        <div className='flex flex-col gap-3'>
          <div className='flex flex-wrap gap-2'>
            <Button
              size='sm'
              color='secondary'
              iconLeading={Key01}
              onClick={openMintConfirm}
              isDisabled={!canMint || isMinting}
            >
              {isMinting ? 'Creating token…' : 'New token'}
            </Button>
            <Button
              size='sm'
              color='secondary-destructive'
              iconLeading={Trash01}
              onClick={openDeleteConfirm}
              isDisabled={!canDelete || isDeleting}
            >
              Delete
            </Button>
          </div>
          {reach === 'org' && (
            <p className='text-xs text-fg-tertiary'>
              Reads every user&apos;s private records of the types it was
              granted. Reach can&apos;t be changed – create a new {noun}
              instead.
            </p>
          )}
        </div>
      </CollapsibleSection>

      <ConfirmationDialog
        isOpen={isMintConfirmOpen}
        title={`Create a new token for ${name}?`}
        description={`The token is shown once. Existing tokens for this ${noun} keep working.`}
        confirmLabel='Create token'
        onConfirm={handleConfirmMint}
        onCancel={closeMintConfirm}
        onClose={closeMintConfirm}
        instance='mint-api-credential-token'
      />
      <WebhookSecretCreatedModal
        isOpen={!!mintedToken}
        onClose={dismissMintedToken}
        data={mintedToken?.data ?? null}
      />
      <ConfirmationDialog
        isOpen={isDeleteConfirmOpen}
        title={`Delete ${noun}?`}
        description={`This ${noun} and its tokens stop working immediately.`}
        variant='destructive'
        confirmLabel='Delete'
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={closeDeleteConfirm}
        onClose={closeDeleteConfirm}
        instance='delete-api-credential'
      />
    </>
  );
}
